import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import {
  CONTENTFUL_PAGE_NAME,
  ContentfulLayout,
  PageType,
} from '../../../shared/constants/Contentful.constants';
import { ICard, ICardWrapper, IContactForm } from '../../../types/shared';

import { baseTrackPageView, DEQ_PAGE_TYPES } from '../../../utils/deq';
import { hashNavigation } from '../../../utils/generic';
import { renderBodyItem } from '../../../utils/generic/renderBodyItem';
import SEO from '../../utils/SEO';

import colors from '../../../styles/colors';
import { StyledContent } from '../../../styles/generic';

import { getRecaptchaSiteKey } from '../../../services/re-captcha.service';
import BaseLayout from '../../layouts/BaseLayout';
import DefaultLayout from '../../layouts/DefaultLayout';
import IsolatedLayout from '../../layouts/IsolatedLayout';
import RestrictedLayout from '../../layouts/RestrictedLayout';
import {
  parseItems,
  parseThemeByColor,
} from '../../molecules/breadcrumbs/Breadcrumbs.helpers';
import ContentWrapper from '../../molecules/content-wrapper/ContentWrapper';
import SubhomeTopComponent from '../../molecules/subhome-top-component/SubhomeTopComponent';
import { CalculatorProps } from '../../organisms/calculator/Calculator.types';
import GenericHeader, {
  AnchorLink,
} from '../../organisms/generic-header/GenericHeader';

import { GenericPageProps } from './GenericPage.types';

const parseAnchorLinks = (
  items: (ICard | ICardWrapper | CalculatorProps)[]
): AnchorLink[] | [] =>
  items
    .filter(({ anchorName }) => anchorName)
    .map(({ anchorName, type, __typename }) => ({
      id: type || __typename,
      label: anchorName || '',
    }));

const GenericPage = ({ data, pageContext }: GenericPageProps) => {
  const { slug, pageType, layoutType } = pageContext;
  const pageData = data.contentfulPageGeneric;
  const {
    metadataTitle,
    metadataDescription,
    metadataImage,
    parentPage,
    pageName,
    showAnchorLinks,
    header,
    headerCards,
    extraHeaderButtons,
    body,
    footer,
    tags,
  } = pageData;
  const anchorLinks = showAnchorLinks
    ? parseAnchorLinks([...body, ...(footer || [])])
    : [];

  useEffect(() => {
    if (
      pageName === CONTENTFUL_PAGE_NAME.veilig ||
      pageName === CONTENTFUL_PAGE_NAME.duurzaam ||
      pageName === CONTENTFUL_PAGE_NAME.slim
    ) {
      baseTrackPageView('page:generic', DEQ_PAGE_TYPES.generic);
    } else if (pageType !== PageType.GATED_CONTACT) {
      baseTrackPageView('page:generic', DEQ_PAGE_TYPES[pageType]);
    }
  }, []);

  const renderHeader = (typeOfPage: string) => {
    if (typeOfPage === PageType.GATED_CONTACT) {
      return (
        <SubhomeTopComponent
          image={header?.image}
          title={header.title}
          subtitle={header?.subtitle}
        />
      );
    }

    const renderBreadcrumbs =
      (layoutType === ContentfulLayout.DEFAULT || !layoutType) &&
      pageType === PageType.PRODUCT;

    return (
      <GenericHeader
        showAnchorLinks={showAnchorLinks}
        anchorLinks={anchorLinks}
        header={header && header}
        headerCard={headerCards?.length ? headerCards[0] : undefined}
        extraPrimaryLink={extraHeaderButtons && extraHeaderButtons[0]}
        extraSecondaryLink={extraHeaderButtons && extraHeaderButtons[1]}
        productName={pageName}
        breadcrumbsProps={
          renderBreadcrumbs
            ? {
                items: parseItems(pageName, parentPage),
                theme: parseThemeByColor(
                  header?.image?.gatsbyImageData?.backgroundColor || ''
                ),
              }
            : undefined
        }
      />
    );
  };

  hashNavigation();

  return (
    <BaseLayout layoutType={layoutType} pageType={pageType}>
      <SEO
        title={metadataTitle}
        description={metadataDescription?.metadataDescription}
        lang={pageData?.metadataLocale}
        image={
          metadataImage.gatsbyImageData?.images?.fallback?.src ||
          metadataImage.file?.url ||
          ''
        }
        noIndex={pageData?.metadataNoIndex}
      />

      {layoutType === ContentfulLayout.ISOLATED && (
        <IsolatedLayout
          header={renderHeader(pageType)}
          content={
            <ContentWrapper id="body">
              <StyledContent type={pageType}>
                {body.map((item, key) =>
                  renderBodyItem(
                    item,
                    key,
                    pageType,
                    tags,
                    slug,
                    parentPage,
                    pageName
                  )
                )}
              </StyledContent>
            </ContentWrapper>
          }
        />
      )}

      {layoutType === ContentfulLayout.RESTRICTED && (
        <RestrictedLayout
          header={renderHeader(pageType)}
          content={
            <ContentWrapper id="body">
              <StyledContent type={pageType}>
                {body.map((item, key) =>
                  renderBodyItem(
                    item,
                    key,
                    pageType,
                    tags,
                    slug,
                    parentPage,
                    pageName
                  )
                )}
              </StyledContent>
            </ContentWrapper>
          }
          footer={
            footer && (
              <Footer id="footer" key="footer">
                <StyledContent>
                  <ContentWrapper>
                    {footer.map((item, key) =>
                      renderBodyItem(
                        item,
                        key,
                        pageType,
                        tags,
                        slug,
                        parentPage,
                        pageName
                      )
                    )}
                  </ContentWrapper>
                </StyledContent>
              </Footer>
            )
          }
        />
      )}

      {(layoutType === ContentfulLayout.DEFAULT || !layoutType) && (
        <DefaultLayout
          header={renderHeader(pageType)}
          content={
            <ContentWrapper id="body">
              <StyledContent type={pageType}>
                {body.map((item, key) =>
                  renderBodyItem(
                    item,
                    key,
                    pageType,
                    tags,
                    slug,
                    parentPage,
                    pageName
                  )
                )}
              </StyledContent>
            </ContentWrapper>
          }
          footer={
            footer && (
              <Footer id="footer" key="footer">
                <StyledContent>
                  <ContentWrapper>
                    {footer.map((item, key) =>
                      renderBodyItem(
                        item,
                        key,
                        pageType,
                        tags,
                        slug,
                        parentPage,
                        pageName
                      )
                    )}
                  </ContentWrapper>
                </StyledContent>
              </Footer>
            )
          }
        />
      )}
    </BaseLayout>
  );
};

const Footer = styled.div`
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  width: inherit;
`;

export const data = graphql`
  query pageGeneric($slug: String) {
    contentfulPageGeneric(slug: { eq: $slug }) {
      id
      pageName
      slug
      ...ParentPageGeneric
      tags {
        id
        name
        value
        type
      }
      metadataDescription {
        metadataDescription
      }
      metadataImage {
        file {
          url
        }
        gatsbyImageData(layout: FULL_WIDTH)
      }
      metadataNoIndex
      metadataTitle
      header {
        ...Card
      }
      extraHeaderButtons {
        ...Link
      }
      headerCards {
        ...Card
      }
      showAnchorLinks
      body {
        ... on ContentfulCompCard {
          ...Card
        }
        ... on ContentfulCompCardWrapper {
          ...CardWrapper
        }
        ... on ContentfulFormContactForm {
          ...ContactForm
        }
        ... on ContentfulCompCalculator {
          ...Calculator
        }
      }
      footer {
        ... on ContentfulCompCard {
          ...Card
        }
        ... on ContentfulCompCardWrapper {
          ...CardWrapper
        }
      }
    }
  }
`;

export default GenericPage;

export function Head() {
  return (
    <script
      async
      src={`https://www.google.com/recaptcha/api.js?render=${getRecaptchaSiteKey()}`}
    />
  );
}
